import { IdcardOutlined, ManOutlined, QuestionCircleOutlined, WomanOutlined } from '@ant-design/icons'
import { NetworkStatus } from '@apollo/client'
import { DatePicker, Divider, Select, Spin } from 'antd'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { BasicProfileInput, useBasicProfileUpdateMutation, useGetBasicProfileQuery } from '@/generated/graphql'

import { EditButton, ProfileInfo } from '../profile-item.component'
import { ProfileInfoProps } from '../profile.interface'
import s from '../profile.module.less'
import { removeTypeName, setProfilePartialAttribute } from '../profile.util'

interface ProfileBasicPartComponentProps {
  userId: number
  isViewer: boolean
  isEdit: boolean
  onEdit: (isEdit: boolean) => void
}

export const BasicProfile: React.FC<ProfileBasicPartComponentProps> = ({ userId, isViewer, isEdit, onEdit }) => {
  const { t } = useTranslation()

  const [editedBasicProfile, setEditedBasicProfile] = React.useState<BasicProfileInput>()

  const { data, refetch, networkStatus } = useGetBasicProfileQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      userId,
    },
  })

  const [basicProfileUpdate, { loading: updateLoading }] = useBasicProfileUpdateMutation({
    onCompleted: () => {
      refetch()
      onEdit(false)
    },
    onError: () => onEdit(false),
  })

  React.useEffect(() => {
    if (data) {
      const basicProfile = cloneDeep(data.user.basicProfile)
      removeTypeName(basicProfile)
      setEditedBasicProfile(basicProfile)
    }
  }, [isEdit])

  const loading = updateLoading || networkStatus === NetworkStatus.refetch
  const basicProfile = data?.user.basicProfile
  const getProfile = (isEdit: boolean) => (isEdit ? editedBasicProfile : basicProfile)

  if (!getProfile(isEdit)) {
    return null
  }

  const gender = [
    <QuestionCircleOutlined key={0} style={{ color: '#ffc53d' }} />,
    <ManOutlined key={1} style={{ color: '#1890ff' }} />,
    <WomanOutlined key={2} style={{ color: '#ff4d4f' }} />,
  ]

  const intros: ProfileInfoProps[] = [
    {
      title: <span>{`${t('profile.basic.name')}:`}</span>,
      value: {
        value: data ? data.user.name + (data.user.alias ? ` (${data.user.alias})` : '') : '',
        isVisible: true,
      },
      attributeChain: 'name',
      readOnly: true,
      disableHidden: true,
    },
    {
      title: <span>{`${t('profile.basic.gender')}:`}</span>,
      value: getProfile(isEdit)!.gender,
      attributeChain: 'gender',
      children: isEdit ? (
        <Select
          style={{ height: !isEdit ? 23 : undefined }}
          disabled={!isEdit}
          value={getProfile(isEdit)!.gender.value}
          onChange={(value) =>
            setProfilePartialAttribute(
              getProfile(isEdit)!,
              getProfile(isEdit)!,
              'gender.value',
              value,
              setEditedBasicProfile
            )
          }
          bordered={isEdit}
          showArrow={false}
        >
          {['0', '1', '2'].map((value) => (
            <Select.Option value={value} key={value}>
              {gender[Number(value)]}
            </Select.Option>
          ))}
        </Select>
      ) : (
        <span className={s.readArea}>{gender[Number(getProfile(isEdit)!.gender.value)]}</span>
      ),
      disableHidden: true,
    },
    {
      title: <span>{`${t('profile.basic.business')}:`}</span>,
      value: getProfile(isEdit)!.business,
      attributeChain: 'business',
      disableHidden: true,
    },
    {
      title: <span>{`${t('profile.basic.educationBackground')}:`}</span>,
      value: getProfile(isEdit)!.educationBackground,
      attributeChain: 'educationBackground',
    },
    {
      title: <span>{`${t('profile.basic.major')}:`}</span>,
      value: getProfile(isEdit)!.major,
      attributeChain: 'major',
    },
    {
      title: <span>{`${t('profile.basic.university')}:`}</span>,
      value: getProfile(isEdit)!.university,
      attributeChain: 'university',
    },
    {
      title: <span>{`${t('profile.basic.hobbies')}:`}</span>,
      value: getProfile(isEdit)!.hobbies,
      attributeChain: 'hobbies',
    },
    {
      title: <span>{`${t('profile.basic.skillSet')}:`}</span>,
      value: getProfile(isEdit)!.skillSet,
      attributeChain: 'skillSet',
    },
    {
      title: <span>{`${t('profile.basic.github')}:`}</span>,
      value: getProfile(isEdit)!.github,
      attributeChain: 'github',
      type: 'link',
      link: getProfile(isEdit)!.github.value,
    },
    {
      title: <span>{`${t('profile.basic.linkedin')}:`}</span>,
      value: getProfile(isEdit)!.linkedin,
      attributeChain: 'linkedin',
      type: 'link',
      link: getProfile(isEdit)!.linkedin.value,
    },
    {
      title: <span>{`${t('profile.basic.beganWorkDate')}:`}</span>,
      value: getProfile(isEdit)!.beganWorkDate,
      attributeChain: 'beganWorkDate',
      children: isEdit ? (
        <DatePicker
          className={s.datePicker}
          value={moment(
            getProfile(isEdit)!.beganWorkDate.value ? getProfile(isEdit)!.beganWorkDate.value : moment.now()
          )}
          onChange={(value) =>
            setProfilePartialAttribute(
              getProfile(isEdit)!,
              getProfile(isEdit)!,
              'beganWorkDate.value',
              moment(value).format('YYYY-MM-DD'),
              setEditedBasicProfile
            )
          }
        />
      ) : (
        <span className={s.readArea}>{getProfile(isEdit)!.beganWorkDate.value}</span>
      ),
    },
    {
      title: <span>{`${t('profile.basic.seatNumber')}:`}</span>,
      value: getProfile(isEdit)!.seatNumber,
      attributeChain: 'seatNumber',
    },
  ]

  const header = (
    <div className={s.sectionHeadContainer}>
      <div className={s.sectionHead}>
        <IdcardOutlined className={s.sectionIcon} />
        <span className={s.sectionTitle}>{t('profile.basic.basicInfo')}</span>
      </div>
      <div className={s.sectionHeadExtra}>
        <EditButton
          isViewer={isViewer}
          isEdit={isEdit}
          loading={loading}
          onEdit={onEdit}
          onUpdate={async () => {
            await basicProfileUpdate({ variables: { basicProfile: editedBasicProfile! } })
          }}
        />
      </div>
    </div>
  )

  const content = (
    <div>
      {intros.map((intro, index) =>
        isEdit || intro.value.isVisible ? (
          <ProfileInfo
            title={intro.title}
            value={intro.value}
            editMode={isEdit}
            key={index}
            onChange={(value: any, chain: string) =>
              setProfilePartialAttribute(getProfile(isEdit)!, getProfile(isEdit)!, chain, value, setEditedBasicProfile)
            }
            attributeChain={intro.attributeChain}
            link={intro.link}
            type={intro.type}
            readOnly={!!intro.readOnly}
            disableHidden={!!intro.disableHidden}
          >
            {intro.children}
          </ProfileInfo>
        ) : null
      )}
    </div>
  )

  return (
    <div className={s.section}>
      {header}
      <Divider className={s.sectionDivider} />
      {loading ? <Spin className={s.spin} spinning={loading} /> : content}
    </div>
  )
}
